import React, { useState, useEffect } from 'react';
import { 
  X, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  AlertCircle,
  CheckCircle,
  Store,
  Loader,
  Plus,
  Star,
  Camera
} from 'lucide-react';
import marketplaceService from '../../services/marketplace.service';
import marketplaceAPI from '../../services/marketplace.api';
import useAuthStore from '../../store/authStore';
import useMarketplaceStore from '../../store/marketplaceStore';
import { BUSINESS_CATEGORIES } from '../../config/categories';
import LocationPicker from '../common/LocationPicker';
import { toast } from 'react-hot-toast';

const EditBusinessModal = ({ business, onClose, onSuccess }) => {
  const { user } = useAuthStore();
  const { businessCategories, getBusinessCategories, isLoading: storeLoading } = useMarketplaceStore();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isCategoriesReady, setIsCategoriesReady] = useState(false);
  
  // Initialize form with existing business data
  const [formData, setFormData] = useState({
    name: business?.name || '',
    description: business?.description || '',
    category: business?.category || '',
    tags: business?.tags || [],
    location: business?.location || '',
    address: business?.address || '',
    city: business?.city || '',
    state: business?.state || '',
    phone: business?.phone || user?.phone || '',
    email: business?.email || user?.email || '',
    website: business?.website || '',
    logoUrl: business?.logoUrl || null,
    coverImageUrl: business?.coverImageUrl || null,
    images: business?.images || [],
    socialLinks: business?.socialLinks || {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: ''
    }
  });

  // Get categories (API first, fallback to static)
  const categories = businessCategories.length > 0 ? businessCategories : BUSINESS_CATEGORIES;

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        // If categories are already in store, use them immediately
        if (businessCategories.length > 0) {
          setIsCategoriesReady(true);
          return;
        }
        
        // Otherwise fetch them
        await getBusinessCategories();
        setIsCategoriesReady(true);
      } catch (error) {
        console.error('Failed to load categories:', error);
        // Still set ready to true to show fallback categories
        setIsCategoriesReady(true);
        toast.error('Failed to load categories, using default list');
      }
    };
    
    loadCategories();
  }, [businessCategories.length, getBusinessCategories]);

  // Set initial state/city/location from business data
  useEffect(() => {
    if (business?.state) {
      setFormData(prev => ({ ...prev, state: business.state }));
    }
    if (business?.city) {
      setFormData(prev => ({ ...prev, city: business.city }));
    }
    if (business?.location) {
      setFormData(prev => ({ ...prev, location: business.location }));
    }
  }, [business?.state, business?.city, business?.location]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSocialLinkChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && formData.tags.length < 10) {
      const newTag = tagInput.trim().toLowerCase();
      if (!formData.tags.includes(newTag)) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageUpload = async (file, type = 'logo') => {
    setUploadingImages(true);
    try {
      const result = await marketplaceService.uploadImage(file);
      
      if (result.success) {
        if (type === 'logo') {
          setFormData(prev => ({ ...prev, logoUrl: result.data.url }));
        } else if (type === 'cover') {
          setFormData(prev => ({ ...prev, coverImageUrl: result.data.url }));
        } else {
          setFormData(prev => ({ 
            ...prev, 
            images: [...prev.images, result.data.url].slice(0, 5) 
          }));
        }
        
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Image upload error:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name?.trim()) errors.name = 'Business name is required';
    if (!formData.category) errors.category = 'Please select a category';
    if (!formData.description?.trim()) errors.description = 'Description is required';
    if (!formData.phone || !/^[0-9]{11}$/.test(formData.phone)) {
      errors.phone = 'Valid phone number is required (11 digits)';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      errors.website = 'Please enter a valid website URL starting with http:// or https://';
    }
    if (!formData.state) errors.state = 'Please select a state';
    if (!formData.city) errors.city = 'Please select a city/LGA';
    if (!formData.address?.trim()) errors.address = 'Address is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsLoading(true);
    try {
      const formattedData = marketplaceService.formatBusinessData(formData);
      await marketplaceAPI.updateBusiness(business.id, formattedData);
      
      toast.success('Business updated successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Business update error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to update business');
    } finally {
      setIsLoading(false);
    }
  };

  // Render category selection
  const renderCategorySelection = () => {
    if (!isCategoriesReady) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader className="h-6 w-6 text-primary-600 animate-spin" />
          <span className="ml-2 text-sm text-gray-500">Loading categories...</span>
        </div>
      );
    }

    if (categories.length === 0) {
      return (
        <div className="bg-yellow-50 rounded-xl p-4 text-center">
          <AlertCircle className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
          <p className="text-sm text-yellow-800">No categories available</p>
          <p className="text-xs text-yellow-600 mt-1">Please contact support</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => handleInputChange('category', category.id)}
            className={`p-3 sm:p-4 rounded-xl text-left transition-all border ${
              formData.category === category.id
                ? 'bg-primary-50 border-primary-200 shadow-neomorph-inset'
                : 'bg-white border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
            }`}
          >
            <div className="flex items-start space-x-3">
              <span className="text-xl">{category.icon}</span>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-800">{category.name}</h4>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{category.description}</p>
              </div>
              {formData.category === category.id && (
                <CheckCircle className="h-4 w-4 text-primary-600 flex-shrink-0" />
              )}
            </div>
          </button>
        ))}
      </div>
    );
  };

  // If categories are still loading, show loading state
  if (!isCategoriesReady) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-neomorph max-w-md w-full p-6 text-center">
          <Loader className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading business categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
      <div className="bg-[#E8ECF5] rounded-2xl shadow-neomorph max-w-3xl w-full my-4 sm:my-8 border border-primary-100/30">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-primary-100/50 sticky top-0 bg-[#E8ECF5] z-10 rounded-t-2xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center mb-1">
                <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Edit Business</h3>
              </div>
              <p className="text-xs text-gray-500 ml-3">Update your business information</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 bg-white rounded-lg p-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6 max-h-[50vh] sm:max-h-[60vh] overflow-y-auto space-y-4 sm:space-y-6">
            {/* Basic Information Section */}
            <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30">
              <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                Basic Information
              </h4>
              
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Tech Solutions NG"
                    className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                      formErrors.name ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                    }`}
                    maxLength={100}
                  />
                  {formErrors.name && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Category *
                  </label>
                  {renderCategorySelection()}
                  {formErrors.category && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.category}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Describe what your business does, services offered, unique selling points..."
                    rows={4}
                    className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                      formErrors.description ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                    }`}
                    maxLength={1000}
                  />
                  {formErrors.description && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.description}</p>
                  )}
                  <div className="mt-1.5 flex flex-wrap justify-between gap-2 text-xs text-gray-500">
                    <span>Brief and clear description works best</span>
                    <span className="bg-[#E8ECF5] px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                      {formData.description.length}/1000
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Business Tags
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      placeholder="Add tags (e.g., web-design, seo)"
                      className="flex-1 px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                      maxLength={20}
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      disabled={!tagInput.trim() || formData.tags.length >= 10}
                      className="bg-white rounded-lg px-3 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-700 border border-gray-100 disabled:opacity-50 flex-shrink-0"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500">Add up to 10 tags to help customers find you</p>
                  
                  {formData.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-lg border border-primary-200 shadow-neomorph-inset"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-1.5 text-primary-600 hover:text-primary-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30">
              <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-4 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full mr-2" />
                Contact Information
              </h4>
              
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    <Phone className="h-3.5 w-3.5 inline mr-1 text-gray-400" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 11))}
                    placeholder="08012345678"
                    className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                      formErrors.phone ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                    }`}
                  />
                  {formErrors.phone && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    <Mail className="h-3.5 w-3.5 inline mr-1 text-gray-400" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="contact@business.com"
                    className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                      formErrors.email ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                    }`}
                  />
                  {formErrors.email && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    <Globe className="h-3.5 w-3.5 inline mr-1 text-gray-400" />
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourbusiness.com"
                    className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                      formErrors.website ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                    }`}
                  />
                  {formErrors.website && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.website}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Social Media Section */}
            <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30">
              <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-4 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full mr-2" />
                Social Media Links
              </h4>
              
              <div className="space-y-3">
                {[
                  { platform: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourpage', icon: '📘' },
                  { platform: 'twitter', label: 'Twitter', placeholder: 'https://twitter.com/yourhandle', icon: '🐦' },
                  { platform: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourprofile', icon: '📷' },
                  { platform: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/yourcompany', icon: '💼' }
                ].map((social) => (
                  <div key={social.platform} className="flex items-center space-x-2">
                    <span className="w-8 text-center text-sm">{social.icon}</span>
                    <input
                      type="url"
                      value={formData.socialLinks[social.platform] || ''}
                      onChange={(e) => handleSocialLinkChange(social.platform, e.target.value)}
                      placeholder={social.placeholder}
                      className="flex-1 px-3 py-2 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Location Section */}
            <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30">
              <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-4 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-2" />
                Location
              </h4>

              <div className="mb-4">
                <LocationPicker
                  selectedState={formData.state}
                  selectedCity={formData.city}
                  onStateChange={(state) => {
                    handleInputChange('state', state);
                    // Auto-update location when state changes
                    if (formData.city) {
                      handleInputChange('location', `${formData.city}, ${state}`);
                    }
                  }}
                  onCityChange={(city) => {
                    handleInputChange('city', city);
                    // Auto-update location when city changes
                    if (formData.state) {
                      handleInputChange('location', `${city}, ${formData.state}`);
                    }
                  }}
                  stateError={formErrors.state}
                  cityError={formErrors.city}
                  label="Business Location"
                  required={true}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  <MapPin className="h-3.5 w-3.5 inline mr-1 text-gray-400" />
                  Street Address *
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="123 Business Street, Area"
                  className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                    formErrors.address ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                  }`}
                />
                {formErrors.address && (
                  <p className="mt-1.5 text-xs text-red-600">{formErrors.address}</p>
                )}
              </div>
            </div>

            {/* Images Section */}
            <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30">
              <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-4 bg-gradient-to-b from-pink-400 to-pink-600 rounded-full mr-2" />
                Business Images
              </h4>
              
              {/* Logo Upload */}
              <div className="mb-6">
                <label className="block text-xs font-medium text-gray-500 mb-2">Logo</label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  {formData.logoUrl ? (
                    <div className="relative">
                      <img
                        src={formData.logoUrl}
                        alt="Business logo"
                        className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl object-cover shadow-neomorph-inset"
                      />
                      <button
                        type="button"
                        onClick={() => handleInputChange('logoUrl', null)}
                        className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-lg p-1 shadow-neomorph-inset hover:shadow-neomorph transition-all"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl bg-[#E8ECF5] shadow-neomorph-inset flex flex-col items-center justify-center hover:shadow-neomorph transition-all border-2 border-dashed border-gray-300 hover:border-primary-400">
                        {uploadingImages ? (
                          <Loader className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 animate-spin" />
                        ) : (
                          <>
                            <Camera className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                            <span className="text-[10px] sm:text-xs text-gray-500 mt-1">Logo</span>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleImageUpload(file, 'logo');
                        }}
                        disabled={uploadingImages}
                      />
                    </label>
                  )}
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Upload your business logo (Square, 200x200px recommended)</p>
                  </div>
                </div>
              </div>

              {/* Cover Image Upload */}
              <div className="mb-6">
                <label className="block text-xs font-medium text-gray-500 mb-2">Cover Image</label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  {formData.coverImageUrl ? (
                    <div className="relative">
                      <img
                        src={formData.coverImageUrl}
                        alt="Business cover"
                        className="h-16 w-24 sm:h-20 sm:w-32 rounded-xl object-cover shadow-neomorph-inset"
                      />
                      <button
                        type="button"
                        onClick={() => handleInputChange('coverImageUrl', null)}
                        className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-lg p-1 shadow-neomorph-inset hover:shadow-neomorph transition-all"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <div className="h-16 w-24 sm:h-20 sm:w-32 rounded-xl bg-[#E8ECF5] shadow-neomorph-inset flex flex-col items-center justify-center hover:shadow-neomorph transition-all border-2 border-dashed border-gray-300 hover:border-primary-400">
                        {uploadingImages ? (
                          <Loader className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 animate-spin" />
                        ) : (
                          <>
                            <Camera className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                            <span className="text-[10px] sm:text-xs text-gray-500 mt-1">Cover</span>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleImageUpload(file, 'cover');
                        }}
                        disabled={uploadingImages}
                      />
                    </label>
                  )}
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Upload a cover image (Banner, 1200x300px recommended)</p>
                  </div>
                </div>
              </div>

              {/* Gallery Images */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">Gallery Images (Max 5)</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {formData.images.map((url, index) => (
                    <div key={index} className="relative">
                      <img
                        src={url}
                        alt={`Business image ${index + 1}`}
                        className="h-20 w-full rounded-xl object-cover shadow-neomorph-inset"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-lg p-1 shadow-neomorph-inset hover:shadow-neomorph transition-all"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  
                  {formData.images.length < 5 && (
                    <label className="cursor-pointer">
                      <div className="h-20 w-full rounded-xl bg-[#E8ECF5] shadow-neomorph-inset flex flex-col items-center justify-center hover:shadow-neomorph transition-all border-2 border-dashed border-gray-300 hover:border-primary-400">
                        {uploadingImages ? (
                          <Loader className="h-5 w-5 text-primary-600 animate-spin" />
                        ) : (
                          <>
                            <Plus className="h-5 w-5 text-gray-400" />
                            <span className="text-[10px] text-gray-500 mt-1">Add</span>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleImageUpload(file, 'gallery');
                        }}
                        disabled={uploadingImages}
                      />
                    </label>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Showcase your business with photos of your products, services, or workspace
                </p>
              </div>
            </div>

            {/* Error Summary */}
            {Object.keys(formErrors).length > 0 && (
              <div className="bg-red-50 rounded-xl p-4 shadow-neomorph-inset border border-red-200/50">
                <div className="flex items-start">
                  <div className="p-1.5 bg-red-100 rounded-md shadow-neomorph-inset mr-3">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-red-800">Please fix the following errors:</h4>
                    <ul className="text-xs text-red-700 mt-1 list-disc list-inside">
                      {Object.values(formErrors).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="p-4 sm:p-6 border-t border-primary-100/50 sticky bottom-0 bg-[#E8ECF5] rounded-b-2xl">
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="order-2 sm:order-1 w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="order-1 sm:order-2 w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all flex items-center justify-center disabled:opacity-50 text-sm"
              >
                {isLoading ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Store className="h-4 w-4 mr-2" />
                    Update Business
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default EditBusinessModal;